import { useEffect, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Text } from "@/components/typography";
import { Button } from "./Button";
import styles from "./ui.module.css";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  children,
  actions,
  className,
}: DialogProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.dialogPanel}>
      <div className={styles.overlay} onClick={onClose} aria-hidden />
      <div
        className={cn(styles.dialog, className)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Dialog"}
      >
        <div className={styles.dialogHeader}>
          <Text role="title" as="h2">
            {title}
          </Text>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            Close
          </Button>
        </div>
        <div className={styles.dialogBody}>{children}</div>
        {actions ? <div className={styles.dialogActions}>{actions}</div> : null}
      </div>
    </div>
  );
}

/** Opinionated confirmation/dialog surface. */
export function Modal(props: DialogProps) {
  return <Dialog {...props} />;
}
